/**
 * The application root: resolve the URL against the registry, draw the answer.
 *
 * Authority: bead scope-njw.
 *
 * There is exactly one place in this application that decides what a URL shows,
 * and it is `resolveRoute`. A screen never re-checks the viewer's role, and a
 * component never renders a panel "if admin" — the tree is the check.
 */
import { Suspense, lazy, useEffect, type LazyExoticComponent } from 'react'

import { resolveRoute, type RouteOutcome } from '../routes/_registry/access.ts'
import { activeRegistry } from '../routes/_registry/registry.active.ts'
import type { RouteComponent, RouteDefinition, RoutePlane } from '../routes/_registry/types.ts'
import { beginLoading } from '../loading/loadingStore.js'
import { Shell, type StaffArea } from '../shell/Shell.tsx'
import NotFound from './screens/NotFound.tsx'
import SignInRequired from './screens/SignInRequired.tsx'
import { useLocationPath } from './navigation.ts'
import { useViewer } from './viewer.ts'
import { LiveRegionManager } from '@nimer/portal-ui/a11y/LiveRegionManager'

/**
 * `lazy()` returns a new component every call, and a new component type
 * unmounts and remounts the tree beneath it. Cached per route id so navigating
 * away and back does not throw the screen's state away.
 */
const screens = new Map<string, LazyExoticComponent<RouteComponent>>()

function screenFor(route: RouteDefinition): LazyExoticComponent<RouteComponent> {
  const cached = screens.get(route.id)
  if (cached !== undefined) return cached

  const created = lazy(route.load)
  screens.set(route.id, created)

  return created
}

/** Chrome follows the route that rendered; a denial is chrome-neutral. */
function planeOf(outcome: RouteOutcome): RoutePlane {
  if (outcome.kind === 'render') return outcome.route.plane
  if (outcome.kind === 'sign-in') return outcome.plane

  return 'public'
}

/**
 * Which staff tree the header's tagline names (spec §3). The admin tree is a
 * separate manifest branch (`admin.manifest.ts`, `admin-console.manifest.ts`)
 * from the consultant tree's, so the branch a route resolved from — not the
 * viewer's role — is what the chrome reflects: an admin browsing `/staff`
 * still sees the consultant console's own tagline.
 */
function staffAreaOf(outcome: RouteOutcome): StaffArea | undefined {
  if (outcome.kind !== 'render' || outcome.route.plane !== 'staff') return undefined

  return outcome.route.branch === 'admin' ? 'admin' : 'consultant'
}

/**
 * A lazy screen still being fetched: announce it through the loading store so
 * the shell overlay narrates the wait, instead of a bare paragraph flashing in
 * the content column. `beginLoading` returns its own cleanup, which is exactly
 * the effect contract.
 */
function RouteLoading() {
  useEffect(() => beginLoading('loading.page'), [])

  return null
}

function Outcome({ outcome }: { readonly outcome: RouteOutcome }) {
  if (outcome.kind === 'not-found') return <NotFound />
  if (outcome.kind === 'sign-in') return <SignInRequired plane={outcome.plane} />

  const Screen = screenFor(outcome.route)

  return (
    <Suspense fallback={<RouteLoading />}>
      <Screen params={outcome.params} />
    </Suspense>
  )
}

export function App() {
  const outcome = resolveRoute(activeRegistry(), useLocationPath(), useViewer())

  /*
   * `LiveRegionManager` wraps the whole tree because `useAnnounce` throws
   * without it — deliberately, per its own docstring: an announcement with
   * nowhere to land is a silent accessibility failure. `ScopePanel` calls it,
   * nothing mounted the provider, and the questionnaire route threw
   * "useAnnounce must be called beneath a <LiveRegionManager>" on every load.
   * It sits outside `Shell` so the regions survive a route change rather than
   * being torn down with the screen that queued the announcement.
   */
  return (
    <LiveRegionManager>
      <Shell plane={planeOf(outcome)} staffArea={staffAreaOf(outcome)}>
        <Outcome outcome={outcome} />
      </Shell>
    </LiveRegionManager>
  )
}
